import { isNotNil } from '@wrisk/ui-components'
import { keyBy } from 'lodash-es'
import { useMemo } from 'react'

import { useProduct } from '../application/product/ProductProvider'
import { ExcessLine, getCoverLevelOption, Quote } from '../domain'
import {
  CoverLevelExcessConfig,
  ExcessCombination,
  ExcessesConfig,
  PolicyExcessConfig,
} from '../state/configuration'

interface Excesses {
  main: ExcessLine[]
  other: ExcessLine[]
  voluntary: Array<{
    voluntaryExcessCode: string
    excesses?: number[]
    excess: number
  }>
  applicable: ExcessLine[]
  combinations: ExcessCombination[] | undefined
}

export const isVoluntaryExcess =
  (voluntaryExcessCode: string) => (voluntary: Excesses['voluntary'][0]) =>
    voluntary.voluntaryExcessCode === voluntaryExcessCode

export const useExcesses = (quote: Quote | undefined): Excesses => {
  const {
    product: { excesses },
  } = useProduct()

  return useMemo(() => {
    const excessesByCategory = keyBy<ExcessLine>(
      quote?.excessLines ?? [],
      'excessCategory',
    )

    switch (excesses?.type) {
      case 'policy': {
        const main = excesses?.main
          ?.map((it) => excessesByCategory[it])
          ?.filter(isNotNil)
          ?.filter((it) => it.mandatoryExcess > 0)

        const other = excesses?.other
          ?.map((it) => excessesByCategory[it])
          ?.filter(isNotNil)

        const voluntary = excesses?.voluntary?.map(
          ({ voluntaryExcessCode, excesses }) =>
            ({
              voluntaryExcessCode,
              excesses,
              excess: quote?.voluntaryExcesses[voluntaryExcessCode] ?? 0,
            } satisfies Excesses['voluntary'][0]),
        )

        return {
          main: main ?? [],
          other: other ?? [],
          voluntary: voluntary ?? [],
          applicable: [],
          combinations: undefined,
        }
      }
      case 'coverLevel': {
        const coverOption = getCoverLevelOption(quote)

        const excessConfig = coverOption
          ? excesses.coverLevels[coverOption.coverLevel]
          : undefined

        const applicable = excessConfig?.main
          ?.map((it) => excessesByCategory[it])
          ?.filter(isNotNil)

        return {
          main: [],
          other: [],
          voluntary: [],
          applicable: applicable ?? [],
          combinations: excessConfig?.combinations,
        }
      }
      default:
        return {
          main: [],
          other: [],
          voluntary: [],
          applicable: [],
          combinations: [],
        }
    }
  }, [quote])
}

export const isCoverLevelExcessConfig = (
  config: ExcessesConfig | undefined,
): config is CoverLevelExcessConfig => config?.type === 'coverLevel'

export const isPolicyExcessConfig = (
  config: ExcessesConfig | undefined,
): config is PolicyExcessConfig => config?.type === 'policy'

export const useExcessConfig =
  <T extends ExcessesConfig>(
    predicate: (config: ExcessesConfig | undefined) => config is T,
  ) =>
  () => {
    const {
      product: { excesses },
    } = useProduct()
    return predicate(excesses) ? excesses : undefined
  }

export const usePolicyExcessConfig = useExcessConfig(isPolicyExcessConfig)
export const useCoverLevelExcessConfig = useExcessConfig(isCoverLevelExcessConfig)
